<template>
  <modal ref="templateModal" @shown="onShown()" id="template-modal" centered hide-footer hide-header content-class="templateModalRectangle">
    <template #default>
      <span class="Masthead">Select template</span>
      <p class="Byline">Work faster with templates! Quickly apply pre-defined settings around your content (customization, security and more!). Set up one from scratch or get started with one below.</p>

      <async-block :loading="loading">
        <b-tabs v-model="selectedTab" fill content-class="mt-3" no-nav-style active-nav-item-class="active-tab" nav-class="nav-class">
          <b-tab title="Default templates">
            <div class="d-flex flex-wrap template-types">
              <template-type tslot="sales" icon="icons-sales.svg" title="Sales intro" class="template-type top-row" :values="salesForm">
                Connect with customers and close more deals with personalized intro videos.
              </template-type>

              <template-type tslot="bug" icon="icons-bug.svg" title="Bug report" class="template-type top-row" :values="bugForm">
                Make bug reporting much easier by showing exactly what you are seeing.
              </template-type>

              <template-type tslot="support" icon="icons-support.svg" title="Support Response" class="template-type" :values="supportForm">
                Speed up support and delight your customers through personal video responses.
              </template-type>

              <template-type tslot="announcement" icon="icons-announcement.svg" title="Announcement" class="template-type" :values="announcementForm">
                Cut through the noise and make your announcement seen and heard.
              </template-type>
            </div>
            <div class="feedback-link-wrapper">
              <a class="feedback-link" href="https://cloudappforms.typeform.com/to/acgkqeJz" target="_blank">Send feedback</a>
            </div>
          </b-tab>

          <b-tab title="Custom templates">
            <a href="#" v-b-toggle.template-sidebar>
              <div class="LayoutTemplate-new-item-background" @click="createNewTemplate()">
                <b-img src="@img/icons-new-transparent.svg" class="icon"/>
                <div class="title">
                  <span class="Template-item-title new-item">Create new</span><br class="short">
                  <span class="Template-item-description new-item">Create your own custom template.</span>
                </div>
                <b-button variant="primary"><b-img src="@img/icons-new-white.svg" class="icon"/>Create</b-button>
              </div>
            </a>

            <div class="LayoutTemplate-item-background" v-for="template in customTemplates" :key="template.slot">
              <div class="content" :class="{ nodescription: !template.attributes.description }" >
                <div class="words">
                  <span class="Template-item-title">{{ template.attributes.name }}</span>
                  <template v-if="template.attributes.description">
                    <br class="short">
                    <span class="Template-item-description">{{ template.attributes.description }}</span>
                  </template>
                </div>
                <div class="spacer">&nbsp;</div>
                <b-img src="@img/icons-settings-gray.svg" alt="Settings" class="gear" @click="clickSettings(template.attributes.slot)" v-b-toggle.template-sidebar/>
                <a href="#" @click="applyTemplate(template.attributes.slot)">
                  <b-button variant="outline-primary"><b-img src="@img/icons-new.svg" class="icon"/>Apply</b-button>
                </a>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </async-block>
    </template>
  </modal>
</template>

<script>
import _ from 'lodash';
import {v4 as uuidv4} from 'uuid';

import Modal from '@cmp/modal';
import TemplateBase from '@cmp/shared/template_base.vue';
import TemplateType from '@cmp/viewer/template_type.vue';

import api from "@/src/api.js";
import track from "@/src/tracking.js";

export default {
  extends: TemplateBase,
  components: { TemplateType, Modal },
  methods: {
    onShown: function() {
      this.getTemplates();
      this.track();
    },
    applyTemplate: function(slot) {
      this.applyTemplateSlot(slot);
    },
    clickSettings: function(template_slot) {
      const template = _.find(this.customTemplates, function(o) { return o.attributes?.slot === template_slot });
      this.$bvModal.hide("template-modal");
      this.$root.$refs.templateSidebar.loadSidebar(this.attrsToForm(template.attributes));
    },
    attrsToForm(template) {
      return {
        templateSlot: template.slot,
        templateName: template.name,
        templateDescription: template.description,
        defaultTitle: template.item_title,
        commentEnabled: template.comment_enabled,
        buttonEnabled: template.button_enabled,
        buttonText: template.button_text,
        buttonLink: template.button_link,
        saved: true
      }
    },
    createNewTemplate: function() {
      const vm = this;

      api.post(vm.$root.routes.api_v5_template_index_path(), vm.customDefaults).
        then(function(response) {
          vm.$bvModal.hide("template-modal");
          vm.$root.$refs.templateSidebar.loadSidebar(vm.attrsToForm(response.data.data.template.attributes));
        });
    },
    getTemplates: function() {
      const vm = this;
      vm.loading = true;
      api.get(vm.$root.routes.api_v5_template_index_path()).
        then(function(response) {
          const templates = response?.data?.data?.templates;

          templates.forEach(template => {
            // Set the 'Apply Template' or 'Configure' button for the pre-defined slots
            if (["sales", "bug", "support", "announcement"].includes(template.attributes.slot)) {
              const form = `${template.attributes.slot}Form`;
              vm.$set(vm[form], "saved", true);
            } else { // Set the title and description for custom slots
              const idx = _.findIndex(vm.customTemplates, { id: template.id });
              vm.$set(vm.customTemplates, idx > -1 ? idx : vm.customTemplates.length, template);
            }
          })
        }).
        catch(function(error) {
          // Quietly ignore errors (incl 404)
        }).
        finally(() => {
          vm.loading = false;
        });
    },
    track: function() {
      track('viewer_templates_modal_viewed', {
        user_id: this.$store.state.user.id,
        item_id: this.$store.state.item.id,
        org_id: this.$store.state.currentOrganization.id,
      });
    }
  },
  watch: {
    customTemplates: function (val) {
      this.selectedTab = val && val.length ? 1 : 0;
    }
  },
  data: function() {
    return {
      loading: true,
      selectedTab: 0,
      customTemplates: [],
      customDefaults: {
        template: {
          slot: uuidv4(),
          name: "Custom template",
          default_title: "My Custom Title",
          reaction_enabled: true,
          comment_enabled: true,
          button_enabled: true
        },
        cta: {
          text: "My Custom Button",
          link: "https://www.example.com/"
        }
      },
      salesForm: {
        templateSlot: "sales",
        templateName: "Sales Intro",
        defaultTitle: "Intro from [Your Company Name Here]",
        reaction_enabled: true,
        commentEnabled: true,
        buttonEnabled: true,
        buttonText: "Book a Meeting",
        buttonLink: "https://calendly.com/URL_HERE",
        saved: false
      },
      bugForm: {
        templateSlot: "bug",
        templateName: "Bug Report",
        defaultTitle: "Bug Report - [Issue Name/Detail]",
        reaction_enabled: true,
        commentEnabled: true,
        buttonEnabled: true,
        buttonText: "View Issue",
        buttonLink: "https://YOUR_COMPANY.atlassian.net/browse/LINKS_HERE",
        saved: false
      },
      supportForm: {
        templateSlot: "support",
        templateName: "Support Response",
        defaultTitle: "Response - Ticket [Number Here]",
        reaction_enabled: true,
        commentEnabled: true,
        buttonEnabled: true,
        buttonText: "Visit Help Center",
        buttonLink: "https://support.getcloudapp.com/",
        saved: false
      },
      announcementForm: {
        templateSlot: "announcement",
        templateName: "Announcement",
        defaultTitle: "Announcing - [Project/Product Name Here]",
        reaction_enabled: true,
        commentEnabled: true,
        buttonEnabled: true,
        buttonText: "Learn More",
        buttonLink: "https://www.getcloudapp.com/blog/new-features-collaboration",
        saved: false
      }
    }
  }
}
</script>

<style lang="scss">
@import "application.scss";

.nav-class {
  li {
    a {
      color: black;
    }
  }
}

.active-tab {
  border: none;
  color: #000;
  font-weight: bold;
//  padding-bottom: 3px;

  &::after {
    content: "";
    display: block;
    padding-top: 3px;
    border-bottom: 2px solid black;
    width: 70%;
    position: relative;
    margin-left: auto;
    margin-right: auto;
  }
}

.modal-content.templateModalRectangle {
  padding: 0.6rem 0.6rem;
  border-radius: 16px;
  box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.08);
  border: solid 0.5px #b5b5b5;
  background-color: $white;
}

.modal-body {
  height: 100%;
}
</style>

<style lang="scss" scoped>
@import "application.scss";

.Masthead {
  width: 14.25rem;
  height: 2.375rem;
  font-family: SFPro;
  font-size: 2rem;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
}

.Byline {
  margin: 1rem 1rem 1rem 0;
  font-family: SFPro;
  font-size: 0.9rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: #000000;
}

.Byline-Copy {
  width: 8.688rem;
  height: 1.5rem;
  margin: 1rem 1rem 1rem 0;
  font-family: SFPro;
  font-size: 1rem;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: #000000;
}

.Combined-Shape {
  width: 2rem;
  height: 2rem;
  margin: 0 1.125rem 0 0;
  background-image: linear-gradient(340deg, #24d292 92%, #d558c8 9%);
}

.LayoutTemplate-item-background {
  width: 28rem;
  height: 5.375rem;
  margin: 1rem 0;
  padding: 0.75rem 1rem 0.938rem;
  border-radius: 8px;
  box-shadow: 0 1px 8px 2px rgba(0, 0, 0, 0.07);
  background-color: $white;

  div.content {
    height: 100%;
    display: flex;
    flex-direction: row;

//    justify-content: center;

    align-items: center;
  }

  div.spacer {
    flex-grow: 1;
  }

  img.gear {
    margin-right: 5px;
    width: 1rem;
    height: 1rem;
  }

  button {
    padding: 0.7rem 1rem;
    margin: 0 0 0 0.313rem;
    font-family: SFPro;
    font-size: 0.75rem;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: center;
  }
}

.LayoutTemplate-new-item-background {
  width: 28rem;
  height: 3.75rem;
  padding: 0.563rem 0.938rem 0.688rem 0.875rem;
  border: 1px solid $bright-blue;
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;

  img.icon {
    height: 2rem;
    width: 2rem;
    margin-right: 1rem;
  }

  button {
    font-size: 0.9rem;
    img.icon {
      height: 0.8rem;
      width: 0.8rem;
      margin-right: 0.5rem;
    }
  }
}

div.title {
  height: 100%;
  flex-grow: 1;
}

.Template-item-title {
  width: 11.313rem;
  height: 1.5rem;
  font-family: SFPro;
  font-size: 1rem;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: #000000;

  &.new-item {
    color: $bright-blue;
  }
}

br.short {
  line-height: 5px;
}

.Template-item-description {
  width: 14.125rem;
  height: 1rem;
  font-family: SFPro;
  font-size: 0.75rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  color: #000000;
  display: block;

  &.new-item {
    color: $bright-blue;
  }
}

.template-types {
  position: relative;
  width: 100%;
  justify-content: space-between;

  .top-row {
    margin-bottom: 1.3rem;
  }
}

.template-type {
  width: 47%;
}

.feedback-link-wrapper {
  font-family: SFPro;
  padding-top: 1rem;
  text-align: right;
}
</style>
